/**
 * Data Saver Utility
 * Sends data directly to backend at submit time. No cache for persistence.
 */

import { HAIR_DETECTION_SESSION_KEY as Hair_DETECTION_SESSION_KEY, HAIR_PENDING_SAVE_KEY as Hair_PENDING_SAVE_KEY } from '../config/hairApp';
import { Hair_API_PREFIX, buildHairApiUrl } from './apiConfig';
import { fetchWithTimeout } from './fetchWithTimeout';

/** Build contact + user payload (snake_case) from form/state data (camelCase or snake_case) */
function toBackendUserContact(userOrContact = {}) {
  return {
    name: userOrContact.name ?? '',
    mobile_number: userOrContact.mobile_number ?? userOrContact.mobileNumber ?? '',
    email: userOrContact.email ?? null,
    instagram_id: userOrContact.instagram_id ?? userOrContact.instagramId ?? null,
    gender: userOrContact.gender ?? null,
    hair_type: userOrContact.hair_type ?? userOrContact.hairType ?? null,
    age: userOrContact.age ?? null
  };
}

const SESSION_STORAGE_KEY = Hair_DETECTION_SESSION_KEY;
const PENDING_SAVE_KEY = Hair_PENDING_SAVE_KEY;
const PENDING_FLUSH_DELAY_MS = 5000;

let _pendingBySession = null;
let _flushTimer = null;

function _loadPending() {
  if (_pendingBySession) return _pendingBySession;
  try {
    const raw = localStorage.getItem(PENDING_SAVE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    _pendingBySession = parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    _pendingBySession = {};
  }
  return _pendingBySession;
}

function _persistPending() {
  try {
    localStorage.setItem(PENDING_SAVE_KEY, JSON.stringify(_pendingBySession || {}));
  } catch (_) {}
}

function _mergeIntoPending(sessionId, patch) {
  if (sessionId == null || String(sessionId).trim() === '') return;
  const sid = String(sessionId).trim();
  const pending = _loadPending();
  const prev = (pending[sid] && typeof pending[sid] === 'object') ? pending[sid] : {};
  pending[sid] = { ...prev, ...patch };
  _persistPending();
}

function _clearPending(sessionId) {
  const sid = sessionId == null ? '' : String(sessionId).trim();
  if (!sid) return;
  const pending = _loadPending();
  if (pending[sid]) {
    delete pending[sid];
    _persistPending();
  }
}

function _scheduleSafetyFlush(sessionId, appSessionToken = null) {
  if (_flushTimer) return;
  _flushTimer = setTimeout(() => {
    _flushTimer = null;
    flushPendingSaveAnalysis(sessionId, appSessionToken).catch(() => {});
  }, PENDING_FLUSH_DELAY_MS);
}

async function saveToBackend(saveData, appSessionToken = null) {
  const payload = { ...saveData };
  // Keep identifiers canonical: if session_id exists, force skin_id to the same value.
  // This prevents stale IDs from overridden/extra payload fields.
  if (payload.session_id != null && String(payload.session_id).trim() !== '') {
    const sid = String(payload.session_id).trim();
    payload.session_id = sid;
    payload.skin_id = sid;
  }
  if (appSessionToken && String(appSessionToken).trim()) {
    payload.app_session_token = String(appSessionToken).trim();
  }
  const response = await fetchWithTimeout(buildHairApiUrl(`${Hair_API_PREFIX}/save-analysis`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    timeoutMs: 20000,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to save data');
  }
  const data = await response.json();
  // Adopt skin_id from response only when it matches what we sent, to prevent an unexpected
  // server response from silently switching the active session to a stale reference.
  if (data && data.skin_id != null) {
    const skinIdStr = String(data.skin_id);
    const sentSid = (payload.session_id != null ? String(payload.session_id) : '').trim();
    const okToAdopt = !sentSid || skinIdStr === sentSid;
    if (okToAdopt && localStorage.getItem(SESSION_STORAGE_KEY) !== skinIdStr) {
      localStorage.setItem(SESSION_STORAGE_KEY, skinIdStr);
    }
  }
  return data;
}

/** Extract only scores from skin conditions for payload size */
function extractScoresOnly(skinConditions) {
  if (!skinConditions || typeof skinConditions !== 'object') return null;
  const keys = ['hydration', 'oxygen', 'smoothness', 'dullness', 'uneven_skintone'];
  const out = {};
  keys.forEach(k => {
    if (skinConditions[k] && typeof skinConditions[k] === 'object')
      out[k] = { score: skinConditions[k].score ?? null };
  });
  return Object.keys(out).length ? out : null;
}

/** Build payload identifier - session_id and skin_id (backend resolves both) */
function toIdentifier(sessionId) {
  if (!sessionId) return {};
  const s = String(sessionId).trim();
  return { session_id: s, skin_id: s };
}

export async function flushPendingSaveAnalysis(sessionId, appSessionToken = null) {
  if (sessionId == null || String(sessionId).trim() === '') return { success: false, error: 'Session/skin ID is required' };
  const sid = String(sessionId).trim();
  const pending = _loadPending();
  const staged = pending[sid];
  if (!staged || typeof staged !== 'object' || Object.keys(staged).length === 0) return { success: true, skipped: true };
  try {
    await saveToBackend({ ...toIdentifier(sid), ...staged }, appSessionToken);
    _clearPending(sid);
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
}

/**
 * Save only device_info for an existing session (e.g. right after creating session on landing CTA).
 * @param {string} sessionId - session_id / skin_id from backend
 * @param {object} deviceInfo - device info from captureDeviceInfo()
 */
export async function saveDeviceInfo(sessionId, deviceInfo, appSessionToken = null) {
  if (!sessionId || String(sessionId).trim() === '') return { success: false, error: 'Session ID required' };
  if (!deviceInfo || typeof deviceInfo !== 'object' || Object.keys(deviceInfo).length === 0) return { success: true };
  try {
    await saveToBackend({ ...toIdentifier(sessionId), device_info: deviceInfo }, appSessionToken);
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
}

/**
 * Save user info (gender, skin_type, age) directly to backend. No cache. Does not send contact fields.
 * @param {string|number} sessionId - session_id or skin_id (numeric string)
 * @param {object} userData - { gender, hairType, age, ... }
 * @param {object} [deviceInfo] - optional device info captured at landing CTA (user_agent, platform, device_type, etc.)
 */
export async function saveUserInfo(sessionId, userData, deviceInfo = null, appSessionToken = null) {
  if (sessionId == null || String(sessionId).trim() === '')
    return { success: false, error: 'Session/skin ID is required' };
  try {
    const payload = {
      ...toIdentifier(sessionId),
      gender: userData.gender ?? null,
      hair_type: userData.hair_type ?? userData.hairType ?? null,
      age: userData.age ?? null
    };
    if (deviceInfo && typeof deviceInfo === 'object' && Object.keys(deviceInfo).length > 0) {
      payload.device_info = deviceInfo;
    }
    await saveToBackend(payload, appSessionToken);
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
}

/**
 * Save contact + demographics in one request (Hair customer info page).
 * @param {object} [extra] - optional questionnaire answers merged into payload (label → value)
 */
export async function saveHairUserProfile(sessionId, profile, deviceInfo = null, appSessionToken = null, extra = null) {
  if (sessionId == null || String(sessionId).trim() === '')
    return { success: false, error: 'Session/skin ID is required' };
  try {
    const { name, mobileNumber, email, gender, hairType, age } = profile || {};
    const payload = {
      ...toIdentifier(sessionId),
      ...toBackendUserContact({ name, mobileNumber, email, gender, hairType, age }),
    };
    if (extra && typeof extra === 'object') {
      // Prevent dynamic form answers from overwriting active session identifiers.
      const sanitizedExtra = { ...extra };
      delete sanitizedExtra.session_id;
      delete sanitizedExtra.skin_id;
      delete sanitizedExtra.Hair_reference;
      delete sanitizedExtra.previous_Hair_reference;
      delete sanitizedExtra.prior_Hair_reference;
      Object.assign(payload, sanitizedExtra);
    }
    if (deviceInfo && typeof deviceInfo === 'object' && Object.keys(deviceInfo).length > 0) {
      payload.device_info = deviceInfo;
    }
    await saveToBackend(payload, appSessionToken);
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
}

/**
 * Save contact + user info directly to backend. Optionally include AQI. No cache.
 * @param {string} sessionId
 * @param {object} contactData - { name, mobileNumber, email, instagramId }
 * @param {object} userInfo - { gender, hairType, age }
 * @param {object} aqiData - optional { success, uv_index, temperature, humidity, wind_speed, location }
 */
export async function saveContactInfo(sessionId, contactData, userInfo = {}, aqiData = null, appSessionToken = null) {
  if (sessionId == null || String(sessionId).trim() === '')
    return { success: false, error: 'Session/skin ID is required' };
  try {
    const payload = {
      ...toBackendUserContact({ ...userInfo, ...contactData })
    };
    if (aqiData && aqiData.success) {
      payload.uv_index = aqiData.uv_index ?? null;
      payload.temperature = aqiData.temperature ?? null;
      payload.humidity = aqiData.humidity ?? null;
      payload.wind_speed = aqiData.wind_speed ?? null;
      payload.location = aqiData.location ?? null;
      payload.latitude = aqiData.latitude ?? null;
      payload.longitude = aqiData.longitude ?? null;
    }
    // Stage (merge) and let a later flush coalesce this with other updates (AQI, detection, goals).
    _mergeIntoPending(sessionId, payload);
    _scheduleSafetyFlush(sessionId, appSessionToken);
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
}

/**
 * Save skin detection results + optional user/contact. No cache.
 * @param {string} sessionId
 * @param {object} skinResults - { detections, skinScore, skinConditions, markedImage }
 * @param {object} userAndContact - optional { name, mobile_number, email, instagram_id, gender, hair_type, age }
 */
export async function saveSkinDetection(sessionId, skinResults, userAndContact = {}, appSessionToken = null) {
  if (sessionId == null || String(sessionId).trim() === '')
    return { success: false, error: 'Session/skin ID is required' };
  try {
    const scoresOnly = extractScoresOnly(skinResults.skinConditions);
    const payload = {
      skin_data: {
        detections: skinResults.detections || [],
        skinScore: skinResults.skinScore ?? null,
        skinConditions: scoresOnly
      },
      ...toBackendUserContact(userAndContact)
    };
    if (skinResults.scanId) {
      payload.scan_id = skinResults.scanId;
    }
    // Flush a single combined request: staged (contact/aqi/etc) + detection payload.
    _mergeIntoPending(sessionId, payload);
    flushPendingSaveAnalysis(sessionId, appSessionToken).catch(() => {});
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
}

/**
 * Save skin goals + optional AQI + user/contact/skinData from state. No cache.
 * @param {string} sessionId
 * @param {object} skinGoalData
 * @param {object} aqiData - optional
 * @param {object} options - { userData, skinResults } from location.state
 */
export async function saveSkinGoals(sessionId, skinGoalData, aqiData = null, options = {}, appSessionToken = null) {
  if (sessionId == null || String(sessionId).trim() === '')
    return { success: false, error: 'Session/skin ID is required' };
  try {
    const { userData = {}, skinResults = null } = options;
    let skinDataToSave = null;
    if (skinResults && (skinResults.detections || skinResults.skinConditions)) {
      skinDataToSave = {
        detections: skinResults.detections || [],
        skinScore: skinResults.skinScore ?? null,
        skinConditions: extractScoresOnly(skinResults.skinConditions)
      };
    }
    const payload = {
      ...toBackendUserContact(userData),
      skin_data: skinDataToSave,
      skin_goal: skinGoalData
    };
    if (aqiData && aqiData.success) {
      payload.uv_index = aqiData.uv_index ?? null;
      payload.temperature = aqiData.temperature ?? null;
      payload.humidity = aqiData.humidity ?? null;
      payload.wind_speed = aqiData.wind_speed ?? null;
      payload.location = aqiData.location ?? null;
      payload.latitude = aqiData.latitude ?? null;
      payload.longitude = aqiData.longitude ?? null;
    }
    _mergeIntoPending(sessionId, payload);
    flushPendingSaveAnalysis(sessionId, appSessionToken).catch(() => {});
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
}

/** Save only AQI/weather update for an existing session. No cache. */
export async function saveAqiUpdate(sessionId, aqiData, appSessionToken = null) {
  if (sessionId == null || !aqiData || !aqiData.success) return { success: false };
  try {
    const payload = {
      uv_index: aqiData.uv_index ?? null,
      temperature: aqiData.temperature ?? null,
      humidity: aqiData.humidity ?? null,
      wind_speed: aqiData.wind_speed ?? null,
      location: aqiData.location ?? null,
      latitude: aqiData.latitude ?? null,
      longitude: aqiData.longitude ?? null
    };
    _mergeIntoPending(sessionId, payload);
    _scheduleSafetyFlush(sessionId, appSessionToken);
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
}
